
import { PushNotifications, Token, ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { db, arrayUnion, serverTimestamp } from './firebase';

/**
 * NotificationService handles mobile push notifications.
 * It manages permissions, registration, and token storage in Firestore.
 */
export const NotificationService = {
    /**
     * Initialize push notifications.
     * Should be called when the user is logged in and on a native platform.
     */
    async initialize(userId: string) {
        if (!Capacitor.isNativePlatform()) {
            console.log('NotificationService: Push notifications are only available on native platforms.');
            return;
        }

        try {
            // 1. Request permissions
            let permStatus = await PushNotifications.checkPermissions();

            if (permStatus.receive === 'prompt') {
                permStatus = await PushNotifications.requestPermissions();
            }

            if (permStatus.receive !== 'granted') {
                console.warn('NotificationService: Push notification permission denied.');
                return;
            }

            // 2. Register with Apple / Google for a token
            await PushNotifications.register();

            // 3. Add listeners
            this.addListeners(userId);

            console.log('NotificationService: Initialized successfully.');
        } catch (error) {
            console.error('NotificationService: Initialization failed:', error);
        }
    },

    /**
     * Add listeners for registration and notification events.
     */
    addListeners(userId: string) {
        // On successful registration, save the token to Firestore
        PushNotifications.addListener('registration', (token: Token) => {
            console.log('NotificationService: Push registration success, token:', token.value);
            this.saveTokenToFirestore(userId, token.value);
        });

        // On registration error
        PushNotifications.addListener('registrationError', (error: any) => {
            console.error('NotificationService: Push registration error:', error);
        });

        // When a notification is received in the foreground
        PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
            console.log('NotificationService: Push received:', notification);
            // You could show a local toast or update UI here if needed
        });

        // When a user taps on a notification
        PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
            console.log('NotificationService: Push action performed:', notification);
            // You could navigate to a specific page here
        });
    },

    /**
     * Save the FCM token to the user's Firestore document.
     * This allows the backend to send notifications to this specific device.
     */
    async saveTokenToFirestore(userId: string, token: string) {
        try {
            // We store tokens in an array to support multiple devices per user
            await db.collection('users').doc(userId).update({
                fcmTokens: arrayUnion(token),
                lastTokenUpdate: serverTimestamp()
            });
            console.log('NotificationService: Token saved to Firestore.');
        } catch (error) {
            console.error('NotificationService: Failed to save token to Firestore:', error);
        }
    },

    /**
     * Clean up listeners.
     */
    async removeAllListeners() {
        if (Capacitor.isNativePlatform()) {
            await PushNotifications.removeAllListeners();
        }
    }
};
